/**
 * 账单管理 Tab - IDS 列表视图 (Pipeline-aware 重构)
 *
 * 核心变化:
 *   - 进度条、阶段标签、状态标签全部从 pipeline.js 派生
 *   - 重试操作统一走 Pipeline API（startPipeline / retryFromStage）
 *   - 批量操作使用 batchPipelineAction
 *   - 移除对外部 settlementApi 和旧 InvoiceDataSourceActionMenu 的依赖
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Table, Tag, Space, Button, Tooltip, Badge, Dropdown, message, Progress, Popconfirm, Input, Modal } from "antd";
import { ReloadOutlined, PlayCircleOutlined, StopOutlined, CheckCircleOutlined, FolderOutlined, LinkOutlined, ScissorOutlined, AuditOutlined, DownOutlined, SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import { useBatchContext } from "../../context/BatchContext";
import { TOTAL_STAGES, parseProgress, getProgressPercent, getStageInfo, getDisplayStatus, getAvailableActions, getBatchRetryMenuItems, inferRetryStage, DISPLAY_STATUS_CONFIG, STAGE_FILTER_CATEGORIES, STATUS_QUICK_FILTERS } from "../../pipeline";
import { SOURCE_TYPE_LABELS, idsIsTerminal } from "../../../constants";
import PermissionGuard from "../../../../../components/auth/PermissionGuard";
import { useTableSelection } from "../../../../../hooks/useTableSelection";
import BatchActionsBar from "../../../../../components/common/BatchActionsBar";
import workbenchApi from "../../services/workbenchApi";
import ConfirmableRiskWarningModal, { normalizeRiskWarnings } from "../ConfirmableRiskWarningModal";
import VoidConfirmModal from "../revision/VoidConfirmModal";
import OcrRetryModeNotice, { getOcrModeRecoveryAction, getOcrRetryOverride, OCR_RETRY_INTENT } from "../OcrRetryModeNotice";
import OcrDpiSlider, { resolveOcrDpi } from "../OcrDpiSlider";
import { getIdsDeleteMode, getSingleDeleteConfirm, summarizeDeleteResults } from "../../utils/deleteSemantics";
import { useAuth } from "../../../../../hooks/useAuth";
import { getReplacementId, getReplacementLabel, getVoidReason } from "../../../utils/revisionDisplay";
import { getProcessingIssueDisplay } from "../../utils/processingIssueDisplay";
import { getInvoiceBalancePresentation, hasInvoiceConversionWarning } from "../../utils/invoiceBalanceDisplay";
import BatchSettlementCurrencyAction from "./BatchSettlementCurrencyAction";
import "./InvoiceListTab.css";

// 无分页表格的默认分页参数（仅用于 useTableSelection 序号计算）
const NO_PAGINATION = { current: 1, pageSize: 9999 };
const idsBatchSelectionDisabled = (record) => idsIsTerminal(record?.status);

export const hasUsableExpectedTotal = (value) => {
    if (value === null || value === undefined || value === "") return false;
    const numericValue = Number(value);
    return Number.isFinite(numericValue);
};

// ---- Pipeline 进度条组件 ----

const PipelineProgress = ({ status }) => {
    const percent = getProgressPercent(status);
    const { phase, currentStage, completedStages } = parseProgress(status);
    const displayStatus = getDisplayStatus(status);

    // 与 DISPLAY_STATUS_CONFIG 颜色一致
    const strokeColorMap = {
        PENDING: "#d9d9d9",
        PROCESSING: "#1890ff", // 蓝
        FAILED: "#ff4d4f", // 红
        BLOCKED: "#fa8c16", // 橙
        REVIEW: "#faad14", // 金黄
        CONFIRMED: "#52c41a", // 绿
        REJECTED: "#ff4d4f", // 红
        VOIDED: "#8c8c8c", // 灰
    };
    const strokeColor = strokeColorMap[displayStatus] || "#1890ff";

    const tooltipText = currentStage ? `${currentStage.shortName} (${completedStages}/${TOTAL_STAGES})` : phase === "terminal" ? `完成 (${TOTAL_STAGES}/${TOTAL_STAGES})` : `待处理 (0/${TOTAL_STAGES})`;

    return (
        <Tooltip title={tooltipText}>
            <Progress percent={percent} size="small" showInfo={false} strokeColor={strokeColor} trailColor="#f0f0f0" style={{ width: 80, margin: 0 }} status={displayStatus === "PROCESSING" ? "active" : undefined} />
        </Tooltip>
    );
};

export const getProcessingIssueReason = (record) => {
    if (record?.processing_issue_reason) {
        return record.processing_issue_reason;
    }

    const structuredData = record?.structured_data || {};
    const failures = Array.isArray(structuredData.gate_check_failures) ? structuredData.gate_check_failures : [];
    const latestGateFailure = [...failures].reverse().find((failure) => failure?.reason);
    if (latestGateFailure?.reason) {
        return latestGateFailure.reason;
    }

    const ocrError = structuredData.ocr_error?.error_message || structuredData.error?.message;
    if (ocrError) {
        return ocrError;
    }

    return null;
};

export const InvoiceStatusCell = ({ record, onOpenDrawer }) => {
    const status = record?.status;
    const ds = getDisplayStatus(status);
    const config = DISPLAY_STATUS_CONFIG[ds] || { label: status, color: "default" };
    const issueReason = getProcessingIssueReason(record);
    const issueDisplay = getProcessingIssueDisplay(issueReason);
    const voidReason = ds === "VOIDED" ? getVoidReason(record) : null;
    const replacementId = ds === "VOIDED" ? getReplacementId(record) : null;
    const replacementLabel = ds === "VOIDED" ? getReplacementLabel(record) : null;

    return (
        <div className="invoice-status-cell">
            <Tag color={config.color}>{config.label}</Tag>
            {issueDisplay && (ds === "FAILED" || ds === "BLOCKED") && (
                <Tooltip title={issueDisplay.tooltip} styles={{ root: { whiteSpace: "pre-line" } }}>
                    <div className="invoice-status-cell__reason">{issueDisplay.label}</div>
                </Tooltip>
            )}
            {voidReason && (
                <Tooltip title={voidReason} styles={{ root: { whiteSpace: "pre-line" } }}>
                    <div className="invoice-status-cell__reason">原因：{voidReason}</div>
                </Tooltip>
            )}
            {replacementId && (
                <Button
                    type="link"
                    size="small"
                    className="invoice-status-cell__replacement"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenDrawer?.({ id: replacementId, original_filename: replacementLabel || replacementId });
                    }}
                >
                    已被 {replacementLabel || replacementId} 替代
                </Button>
            )}
        </div>
    );
};

const OcrRecoveryConfirmContent = ({ intent, ocrOptions, onDpiChange }) => {
    const [dpi, setDpi] = useState(resolveOcrDpi(ocrOptions));
    const isHighResolution = intent === OCR_RETRY_INTENT.HIGH_RESOLUTION;
    const handleDpiChange = useCallback((nextDpi) => {
        setDpi(nextDpi);
        onDpiChange?.(nextDpi);
    }, [onDpiChange]);

    return (
        <>
            <OcrRetryModeNotice intent={intent} ocrOptions={ocrOptions} pdfRenderDpi={dpi} />
            {isHighResolution && <OcrDpiSlider value={dpi} onChange={handleDpiChange} />}
        </>
    );
};

// ---- 单行操作菜单 ----

export const RowActionMenu = ({ record, onOpenDrawer, onDelete, onVoidWithReparse }) => {
    const { startPipeline, retryFromStage, stopPipeline } = useBatchContext();
    const { hasPermission } = useAuth();
    const [advancing, setAdvancing] = useState(false);
    const actions = getAvailableActions(record.status);
    const displayStatus = getDisplayStatus(record.status);
    const deleteMode = getIdsDeleteMode(record);
    const deleteConfirm = getSingleDeleteConfirm(record);
    const canUpdate = hasPermission("settlement_ap_invoice_data_source:update");
    const canDelete = hasPermission("settlement_ap_invoice_data_source:delete");
    const canRevise = hasPermission("settlement:revision:write");

    const continueNextAction = actions.find((a) => a.key === "continue_next");
    const continueNextStageId = continueNextAction?.stageId;

    const handleAdvanceToNext = useCallback(async () => {
        if (!continueNextStageId) return;
        setAdvancing(true);
        try {
            await retryFromStage(record.id, continueNextStageId);
        } finally {
            setAdvancing(false);
        }
    }, [record.id, retryFromStage, continueNextStageId]);

    const handleAction = useCallback(
        async (action) => {
            if (action.key === "start") {
                await startPipeline(record.id);
            } else if (action.key === "stop") {
                await stopPipeline(record.id);
            } else if (action.key.startsWith("retry_") || action.key.startsWith("rerun_")) {
                await retryFromStage(record.id, action.stageId);
            }
        },
        [record.id, startPipeline, retryFromStage, stopPipeline],
    );

    const isFailureRecovery = displayStatus === "FAILED" || displayStatus === "BLOCKED";
    const primaryAction = isFailureRecovery
        ? actions.find((action) => action.key === "retry_current") || actions[0]
        : actions[0];
    const moreActions = actions.filter((action) => action.key !== primaryAction?.key && action.key !== "continue_next");
    const canRetryOcr = actions.some((action) => action.stageId === "ocr" && action.key !== "start");
    const ocrRecoveryAction = getOcrModeRecoveryAction(record.ocr_options);

    const primaryActionLabel = isFailureRecovery ? "从失败阶段重试" : primaryAction?.label;
    const primaryActionTooltip = isFailureRecovery
        ? `从失败阶段重试：将从当前失败或受阻阶段重新执行（${primaryAction?.label || "自动推断阶段"}）。`
        : null;

    const handlePrimaryAction = useCallback(async () => {
        if (!primaryAction) return;
        if (isFailureRecovery) {
            await retryFromStage(record.id, inferRetryStage(record.status));
            return;
        }
        await handleAction(primaryAction);
    }, [handleAction, isFailureRecovery, primaryAction, record.id, record.status, retryFromStage]);

    const moreMenuItems = canUpdate
        ? moreActions.map((a) => ({
            key: a.key,
            label: a.label,
            danger: a.danger,
            onClick: () => handleAction(a),
        }))
        : [];

    if (canUpdate && canRetryOcr) {
        if (moreMenuItems.length > 0) {
            moreMenuItems.push({ type: "divider" });
        }
        moreMenuItems.push({
            key: ocrRecoveryAction.key,
            label: ocrRecoveryAction.label,
            onClick: () => {
                let selectedDpi = resolveOcrDpi(record.ocr_options);
                const override = getOcrRetryOverride(ocrRecoveryAction.intent);
                Modal.confirm({
                    title: ocrRecoveryAction.label,
                    content: (
                        <OcrRecoveryConfirmContent
                            intent={ocrRecoveryAction.intent}
                            ocrOptions={record.ocr_options}
                            onDpiChange={(nextDpi) => { selectedDpi = nextDpi; }}
                        />
                    ),
                    okText: "开始识别",
                    cancelText: "取消",
                    onOk: () => {
                        const retryArgs = [record.id, "ocr", undefined, override];
                        if (override === true) retryArgs.push(selectedDpi);
                        return retryFromStage(...retryArgs);
                    },
                });
            },
        });
    }

    if (canRevise && record.status === "CONFIRMED") {
        if (moreMenuItems.length > 0 && moreMenuItems[moreMenuItems.length - 1].type !== "divider") {
            moreMenuItems.push({ type: "divider" });
        }
        moreMenuItems.push({
            key: "void-with-reparse",
            label: "作废并重发",
            danger: true,
            onClick: () => onVoidWithReparse?.(record),
        });
    }

    if (canDelete) {
        if (moreMenuItems.length > 0 && moreMenuItems[moreMenuItems.length - 1].type !== "divider") {
            moreMenuItems.push({ type: "divider" });
        }
        moreMenuItems.push({
            key: "delete",
            label: "删除",
            danger: true,
            disabled: deleteMode === "protected",
            onClick: () => {
                Modal.confirm({
                    title: deleteConfirm.title,
                    content: deleteConfirm.description,
                    onOk: () => onDelete?.(record),
                    okText: deleteConfirm.okText,
                    okButtonProps: { danger: true },
                    cancelText: "取消",
                });
            },
        });
    }

    if (actions.length === 0 && displayStatus !== "REVIEW") {
        return (
            <Space size={8} onClick={(e) => e.stopPropagation()}>
                <Button type="link" size="small" onClick={() => onOpenDrawer?.(record)}>
                    详情
                </Button>
                {moreMenuItems.length > 0 && (
                    <Dropdown menu={{ items: moreMenuItems }} trigger={["click"]}>
                        <Button type="link" size="small">
                            更多 <DownOutlined style={{ fontSize: 10 }} />
                        </Button>
                    </Dropdown>
                )}
            </Space>
        );
    }

    return (
        <Space size={8} onClick={(e) => e.stopPropagation()}>
            <Button type="link" size="small" onClick={() => onOpenDrawer?.(record)}>
                详情
            </Button>
            {canUpdate && continueNextAction && (
                <Button
                    type="link"
                    size="small"
                    loading={advancing}
                    onClick={handleAdvanceToNext}
                >
                    {continueNextAction.label}
                </Button>
            )}
            {canUpdate && primaryAction &&
                (primaryAction.confirm ? (
                    <Popconfirm title={primaryAction.confirm} onConfirm={handlePrimaryAction} okText="确认" cancelText="取消">
                        <Button type="link" size="small" danger={primaryAction.danger}>
                            {primaryActionLabel}
                        </Button>
                    </Popconfirm>
                ) : (
                    <Tooltip title={primaryActionTooltip}>
                        <Button type="link" size="small" danger={primaryAction.danger} onClick={handlePrimaryAction}>
                            {primaryActionLabel}
                        </Button>
                    </Tooltip>
                ))}
            {moreMenuItems.length > 0 && (
                <Dropdown menu={{ items: moreMenuItems }} trigger={["click"]}>
                    <Button type="link" size="small">
                        更多 <DownOutlined style={{ fontSize: 10 }} />
                    </Button>
                </Dropdown>
            )}
        </Space>
    );
};

export const InvoiceFilenameCell = ({ text, record }) => {
    if (record.record_type === "FILE_GROUP") {
        const fileCount = record.group_files?.length || 0;
        const groupName = text || "未命名组";
        return (
            <div className="invoice-file-cell invoice-file-cell--group">
                <FolderOutlined className="invoice-file-cell__icon" style={{ color: "#fa8c16", fontSize: 16 }} />
                <Tooltip title={`文件组: ${groupName}`}>
                    <span className="invoice-file-cell__name invoice-file-cell__name--strong">{groupName}</span>
                </Tooltip>
                <Tag className="invoice-file-cell__tag" color="orange" style={{ margin: 0 }}>
                    {fileCount}个文件
                </Tag>
            </div>
        );
    }

    const displayName = text || SOURCE_TYPE_LABELS[record.source_type] || "未知文件";
    return (
        <div className="invoice-file-cell">
            <Tooltip title={displayName}>
                <span className="invoice-file-cell__name">{displayName}</span>
            </Tooltip>
            {record.file_group_id && !record.record_type && (
                <Tooltip title={`属于文件组: ${record.file_group_name || "未命名组"}`}>
                    <LinkOutlined className="invoice-file-cell__icon" style={{ color: "#1890ff", fontSize: 14 }} />
                </Tooltip>
            )}
            {record.is_split && (
                <Tooltip title={`拆分文件 (来自: ${record.split_from_filename || "原始文件"}, 页面: ${record.split_pages || "未知"})`}>
                    <ScissorOutlined className="invoice-file-cell__icon" style={{ color: "#52c41a", fontSize: 14 }} />
                </Tooltip>
            )}
        </div>
    );
};

// ---- 主组件 ----

const InvoiceListTab = ({ onOpenDrawer }) => {
    const { idsList, pollRefresh, refreshBatch, batchNumber, loading: batchLoading, batchPipelineAction, stopPipeline, deleteInvoiceDataSources, batchSetSettlementCurrency, triggerBatchValidation, batchValidationReadiness } = useBatchContext();

    const [activeStageFilter, setActiveStageFilter] = useState("all");
    const [activeStatusFilter, setActiveStatusFilter] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [batchActioning, setBatchActioning] = useState(false);
    const [batchRiskWarningModal, setBatchRiskWarningModal] = useState(null);
    const [voidModal, setVoidModal] = useState({ open: false, record: null, impact: null });
    const [voidImpactLoading, setVoidImpactLoading] = useState(false);
    const pollTimerRef = useRef(null);

    // ---- 轮询：有 PROCESSING 时每 5s 静默刷新 ----
    // 使用 pollRefresh 而非 refreshBatch，差异：
    //   pollRefresh: 无 loading spinner、有 diff 检测（状态没变不触发 setState/级联）
    //   refreshBatch: 有 loading spinner（仅用于用户手动刷新）
    const hasProcessing = useMemo(() => idsList.some((item) => getDisplayStatus(item.status) === "PROCESSING"), [idsList]);

    useEffect(() => {
        if (hasProcessing) {
            pollTimerRef.current = setInterval(() => {
                pollRefresh();
            }, 5000);
        }
        return () => {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        };
    }, [hasProcessing, pollRefresh]);

    // ---- 三维复合筛选：阶段 × 状态 × 文本搜索（AND 逻辑） ----
    const filteredList = useMemo(() => {
        const stageFn = STAGE_FILTER_CATEGORIES[activeStageFilter]?.filter;
        const statusFn = STATUS_QUICK_FILTERS[activeStatusFilter]?.filter;
        const needle = searchText.trim().toLowerCase();

        const matchedItems = idsList.filter((item) => {
            // 维度 1: 阶段
            if (stageFn && activeStageFilter !== "all" && !stageFn(item.status)) return false;
            // 维度 2: 状态
            if (statusFn && activeStatusFilter !== "all" && !statusFn(item.status)) return false;
            // 维度 3: 文本搜索（文件名 + 账单号模糊匹配）
            if (needle) {
                const filename = (item.original_filename || "").toLowerCase();
                const invoiceNumbers = item.invoice_summary?.invoice_numbers || [];
                const matchesFilename = filename.includes(needle);
                const matchesInvoice = invoiceNumbers.some((n) => n.toLowerCase().includes(needle));
                if (!matchesFilename && !matchesInvoice) return false;
            }
            return true;
        });

        const activeItems = [];
        const voidedItems = [];
        matchedItems.forEach((item) => {
            if (getDisplayStatus(item.status) === "VOIDED") {
                voidedItems.push(item);
            } else {
                activeItems.push(item);
            }
        });
        return [...activeItems, ...voidedItems];
    }, [idsList, activeStageFilter, activeStatusFilter, searchText]);

    // 使用系统统一的表格选择 Hook（序号列 + Checkbox + 拖拽多选）
    const {
        selectedRowKeys,
        selectedRows,
        tableRef: selectionTableRef,
        rowSelection,
        getRowProps,
        clearSelection,
        getIndexColumn,
        handleRowSelectionChange,
    } = useTableSelection({
        rowKey: "id",
        dataSource: filteredList,
        pagination: NO_PAGINATION,
        isRowSelectionDisabled: idsBatchSelectionDisabled,
    });

    // 全选当前可见行
    const handleSelectAllVisible = useCallback(() => {
        const visibleKeys = filteredList.map((item) => item.id);
        handleRowSelectionChange(visibleKeys, filteredList);
    }, [filteredList, handleRowSelectionChange]);

    // 阶段筛选计数（基于全量数据）
    const stageCounts = useMemo(() => {
        const counts = {};
        Object.keys(STAGE_FILTER_CATEGORIES).forEach((key) => {
            if (key === "all") {
                counts[key] = idsList.length;
            } else {
                counts[key] = idsList.filter((item) => STAGE_FILTER_CATEGORIES[key].filter(item.status)).length;
            }
        });
        return counts;
    }, [idsList]);

    // 状态筛选计数（基于全量数据）
    const statusCounts = useMemo(() => {
        const counts = {};
        Object.keys(STATUS_QUICK_FILTERS).forEach((key) => {
            if (key === "all") {
                counts[key] = idsList.length;
            } else {
                counts[key] = idsList.filter((item) => STATUS_QUICK_FILTERS[key].filter(item.status)).length;
            }
        });
        return counts;
    }, [idsList]);

    // ---- 批量操作 ----
    const handleBatchRetry = useCallback(
        async (stageKey) => {
            if (selectedRowKeys.length === 0) return;
            setBatchActioning(true);

            if (stageKey === "auto") {
                // 智能重试：逐个推断阶段
                let successCount = 0;
                let failCount = 0;
                for (const id of selectedRowKeys) {
                    const item = idsList.find((i) => i.id === id);
                    if (!item) continue;
                    const targetStage = inferRetryStage(item.status);
                    const result = await batchPipelineAction([id], "retry", targetStage, { silent: true });
                    successCount += result?.successCount || 0;
                    failCount += result?.failCount || 0;
                }
                let msg = `智能重试完成: ${successCount} 成功`;
                if (failCount > 0) msg += `, ${failCount} 失败`;
                message.info(msg);
            } else {
                // 指定阶段的批量重试
                await batchPipelineAction(selectedRowKeys, "retry", stageKey);
            }

            setBatchActioning(false);
            clearSelection();
        },
        [selectedRowKeys, idsList, batchPipelineAction, clearSelection],
    );

    const handleBatchStop = useCallback(async () => {
        if (selectedRowKeys.length === 0) return;
        setBatchActioning(true);
        let count = 0;
        for (const id of selectedRowKeys) {
            try {
                await stopPipeline(id);
                count++;
            } catch {
                /* skip */
            }
        }
        setBatchActioning(false);
        clearSelection();
        message.info(`已停止 ${count} 个任务`);
    }, [selectedRowKeys, stopPipeline, clearSelection]);

    const showDeleteResults = useCallback((payload, options = {}) => {
        const { single = false } = options;
        const results = payload?.results || [];
        const summary = summarizeDeleteResults(results);

        if (summary.failed === 0) {
            const stoppedText = summary.stoppedAndDeleted > 0 ? `，其中 ${summary.stoppedAndDeleted} 张已先停止关联执行` : "";
            message.success(single ? `账单已删除${stoppedText}` : `批量删除完成: ${summary.succeeded} 张成功${stoppedText}`);
            return;
        }

        const renderItem = (item) => {
            const record = idsList.find((candidate) => String(candidate.id) === String(item.id));
            const name = record?.original_filename || item.id;
            return (
                <li key={item.id} style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 500 }}>{name}</div>
                    <div style={{ color: item.success ? "#52c41a" : "#ff4d4f", fontSize: 12 }}>
                        {item.message || (item.success ? "删除成功" : "删除失败")}
                    </div>
                </li>
            );
        };

        Modal.info({
            title: single ? "账单删除结果" : "批量删除结果",
            width: 560,
            content: (
                <div style={{ maxHeight: 420, overflowY: "auto", marginTop: 12 }}>
                    <p>
                        成功: <span style={{ color: "#52c41a", fontWeight: 600 }}>{summary.succeeded}</span> 张，
                        失败: <span style={{ color: "#ff4d4f", fontWeight: 600 }}>{summary.failed}</span> 张
                        {summary.stoppedAndDeleted > 0 && <>，停止后删除: <span style={{ color: "#1890ff", fontWeight: 600 }}>{summary.stoppedAndDeleted}</span> 张</>}
                        {summary.protected > 0 && <>，已确认/已作废阻断: <span style={{ color: "#fa8c16", fontWeight: 600 }}>{summary.protected}</span> 张</>}
                        {summary.stopFailed > 0 && <>，停止失败: <span style={{ color: "#ff4d4f", fontWeight: 600 }}>{summary.stopFailed}</span> 张</>}
                    </p>
                    <ul style={{ paddingLeft: 20, margin: 0 }}>{results.map(renderItem)}</ul>
                </div>
            ),
            okText: "知道了",
        });
    }, [idsList]);

    const executeDeleteRecords = useCallback(async (records, options = {}) => {
        const targets = records.filter((record) => record?.id);
        if (targets.length === 0) return;
        setBatchActioning(true);
        try {
            const payload = await deleteInvoiceDataSources(targets.map((record) => record.id));
            showDeleteResults(payload, options);
            if ((payload?.succeeded || 0) > 0) {
                clearSelection();
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "删除失败";
            message.error(detail);
        } finally {
            setBatchActioning(false);
        }
    }, [deleteInvoiceDataSources, showDeleteResults, clearSelection]);

    const handleSingleDelete = useCallback((record) => {
        executeDeleteRecords([record], { single: true });
    }, [executeDeleteRecords]);

    const handleBatchDelete = useCallback(() => {
        if (selectedRowKeys.length === 0) return;
        const records = selectedRowKeys.map((id) => selectedRows.find((row) => row.id === id) || idsList.find((row) => row.id === id) || { id });
        const runningCount = records.filter((record) => getIdsDeleteMode(record) === "running").length;
        const protectedCount = records.filter((record) => getIdsDeleteMode(record) === "protected").length;

        Modal.confirm({
            title: "批量删除确认",
            width: 560,
            content: (
                <div>
                    <p>将对选中的 {records.length} 张账单执行删除。后端会逐条校验，成功项会删除，失败项会保留。</p>
                    {runningCount > 0 && <p>其中 {runningCount} 张仍在处理，删除前会先停止并失效关联执行；停止失败则不会删除。</p>}
                    {protectedCount > 0 && <p>其中 {protectedCount} 张已确认入库或已作废，后端会阻断删除。</p>}
                </div>
            ),
            okText: "确认删除",
            okButtonProps: { danger: true },
            cancelText: "取消",
            onOk: () => executeDeleteRecords(records),
        });
    }, [selectedRowKeys, selectedRows, idsList, executeDeleteRecords]);

    const handleOpenVoidWithReparse = useCallback(async (record) => {
        if (!record?.id) return;
        if (record.status !== "CONFIRMED") {
            message.warning("只有已审核账单可以作废并重发");
            return;
        }
        setVoidImpactLoading(true);
        try {
            const impact = await workbenchApi.assessRevisionImpact(record.id, "IDS");
            setVoidModal({ open: true, record, impact });
        } catch (err) {
            // 后端 RevisionError 的信息在 data.error；data.detail 是 HTTPException(500) 的字段
            const detail = err?.response?.data?.error || err?.response?.data?.detail || err.message || "影响评估失败";
            message.error(detail);
        } finally {
            setVoidImpactLoading(false);
        }
    }, []);

    const handleConfirmVoidWithReparse = useCallback(async (reason, mode) => {
        if (!voidModal.record?.id) return;
        const selectedMode = mode || "copy";
        setBatchActioning(true);
        try {
            const result = await workbenchApi.voidInvoiceWithReparse(voidModal.record.id, reason, selectedMode);
            const newIdsId = result?.new_ids_id;
            const successMessage = (result?.mode || selectedMode) === "copy"
                ? "已作废，新账单已创建并复制了原数据，请前往审核并修改错误项"
                : newIdsId ? `已作废，新账单 ${newIdsId} 已创建，请前往处理` : "已作废并创建新账单";
            message.success(successMessage);
            setVoidModal({ open: false, record: null, impact: null });
            await refreshBatch();
        } catch (err) {
            const detail = err?.response?.data?.error || err?.response?.data?.detail || err.message || "作废并重发失败";
            message.error(detail);
        } finally {
            setBatchActioning(false);
        }
    }, [refreshBatch, voidModal.record]);

    const runBatchCommitRef = useRef(null);

    const runBatchCommit = useCallback(async (eligibleIds, options = {}) => {
        const { ignoreWarnings = false, previousBlockingFailedItems = [] } = options;
        setBatchActioning(true);
        try {
            const result = await workbenchApi.batchCommit(eligibleIds, ignoreWarnings);
            const { succeeded = 0, failed = 0, results = [] } = result.data || {};
            const failedItems = results.filter((r) => !r.success);
            const isRiskWarningItem = (item) => (Array.isArray(item.risk_warnings) && item.risk_warnings.length > 0) || (Array.isArray(item.risk_warning_details) && item.risk_warning_details.length > 0);
            const riskWarningItems = failedItems.filter(isRiskWarningItem);
            const blockingFailedItems = failedItems.filter((item) => !isRiskWarningItem(item));

            const renderFailedItem = (item, color = "#ff4d4f") => {
                const record = idsList.find((i) => i.id === item.ids_id);
                const name = record?.original_filename || item.ids_id;
                const warnings = normalizeRiskWarnings(item.risk_warnings, item.risk_warning_details);
                const messageText = warnings.length > 0 ? String(item.message || "存在风控警告").split("\n")[0] : item.message;

                return (
                    <li key={item.ids_id} style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: 500 }}>{name}</div>
                        <div style={{ color, fontSize: 12, whiteSpace: "pre-line", lineHeight: "1.5" }}>{messageText}</div>
                        {warnings.length > 0 && (
                            <ul style={{ margin: "4px 0 0", paddingLeft: 18, color: "#fa8c16", fontSize: 12 }}>
                                {warnings.map((warning, index) => (
                                    <li key={`${item.ids_id}-${index}`}>{warning.message}</li>
                                ))}
                            </ul>
                        )}
                    </li>
                );
            };

            const renderResultContent = (items, options = {}) => {
                const { showRiskSummary = false } = options;
                return (
                    <div style={{ maxHeight: 420, overflowY: "auto", marginTop: 12 }}>
                        <p>
                            成功: <span style={{ color: "#52c41a", fontWeight: 600 }}>{succeeded}</span> 张
                            {showRiskSummary && riskWarningItems.length > 0 && (
                                <>
                                    ，需确认: <span style={{ color: "#fa8c16", fontWeight: 600 }}>{riskWarningItems.length}</span> 张
                                </>
                            )}
                            {blockingFailedItems.length > 0 && (
                                <>
                                    ，失败: <span style={{ color: "#ff4d4f", fontWeight: 600 }}>{blockingFailedItems.length}</span> 张
                                </>
                            )}
                            {!showRiskSummary && failed > 0 && blockingFailedItems.length === 0 && (
                                <>
                                    ，失败: <span style={{ color: "#ff4d4f", fontWeight: 600 }}>{failed}</span> 张
                                </>
                            )}
                        </p>
                        {items.length > 0 && (
                            <>
                                <p style={{ marginBottom: 8, fontWeight: 500 }}>{options.title || "失败明细："}</p>
                                <ul style={{ paddingLeft: 20, margin: 0 }}>{items.map((item) => renderFailedItem(item, options.color))}</ul>
                            </>
                        )}
                        {showRiskSummary && blockingFailedItems.length > 0 && (
                            <>
                                <p style={{ margin: "12px 0 8px", fontWeight: 500 }}>仍需修正的失败项：</p>
                                <ul style={{ paddingLeft: 20, margin: 0 }}>{blockingFailedItems.map((item) => renderFailedItem(item))}</ul>
                            </>
                        )}
                    </div>
                );
            };

            if (failed === 0) {
                if (previousBlockingFailedItems.length > 0) {
                    Modal.info({
                        title: "批量审核结果",
                        width: 640,
                        content: (
                            <div style={{ maxHeight: 420, overflowY: "auto", marginTop: 12 }}>
                                <p>
                                    可确认风险项已提交: <span style={{ color: "#52c41a", fontWeight: 600 }}>{succeeded}</span> 张；首轮仍有 <span style={{ color: "#ff4d4f", fontWeight: 600 }}>{previousBlockingFailedItems.length}</span> 张硬阻断失败项需修正。
                                </p>
                                <ul style={{ paddingLeft: 20, margin: 0 }}>{previousBlockingFailedItems.map((item) => renderFailedItem(item))}</ul>
                            </div>
                        ),
                        okText: "知道了",
                    });
                } else {
                    message.success(`批量审核完成: ${succeeded} 张账单全部成功`);
                }
            } else if (!ignoreWarnings && riskWarningItems.length > 0) {
                const warningEntries = riskWarningItems.flatMap((item) => {
                    const record = idsList.find((i) => i.id === item.ids_id);
                    const subject = record?.original_filename || item.ids_id;
                    return normalizeRiskWarnings(item.risk_warnings, item.risk_warning_details).map((warning) => ({
                        ...warning,
                        subject,
                    }));
                });
                setBatchRiskWarningModal({
                    items: riskWarningItems,
                    blockingFailedItems,
                    succeeded,
                    warnings: warningEntries,
                });
            } else {
                // 展示汇总结果 Modal
                Modal.info({
                    title: "批量审核结果",
                    width: 560,
                    content: renderResultContent(failedItems),
                    okText: "知道了",
                });
            }

            clearSelection();
            refreshBatch();
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "批量审核失败";
            message.error(detail);
        } finally {
            setBatchActioning(false);
        }
    }, [idsList, refreshBatch, clearSelection]);

    runBatchCommitRef.current = runBatchCommit;

    const handleBatchCommit = useCallback(async () => {
        if (selectedRowKeys.length === 0) return;

        // 过滤出处于 REVIEW 状态的可审核项
        const eligibleIds = selectedRowKeys.filter((id) => {
            const item = idsList.find((i) => i.id === id);
            return item && getDisplayStatus(item.status) === "REVIEW";
        });

        if (eligibleIds.length === 0) {
            message.warning("选中的账单中没有待审核的项");
            return;
        }

        if (eligibleIds.length < selectedRowKeys.length) {
            Modal.confirm({
                title: "批量审核确认",
                content: `共选中 ${selectedRowKeys.length} 张账单，其中 ${eligibleIds.length} 张处于待审核状态。仅对符合条件的 ${eligibleIds.length} 张执行审核通过，是否继续？`,
                okText: "确认审核",
                cancelText: "取消",
                onOk: async () => await runBatchCommit(eligibleIds),
            });
        } else {
            Modal.confirm({
                title: "批量审核确认",
                content: `将对 ${eligibleIds.length} 张账单执行批量审核通过，是否继续？`,
                okText: "确认审核",
                cancelText: "取消",
                onOk: async () => await runBatchCommit(eligibleIds),
            });
        }
    }, [selectedRowKeys, idsList, runBatchCommit]);

    // 批量重试下拉菜单 — 从 Pipeline 定义生成
    const retryMenuItems = useMemo(() => getBatchRetryMenuItems(idsList), [idsList]);
    const retryMenuProps = {
        items: retryMenuItems.map((item) => ({
            ...item,
            onClick: item.key && item.key !== "divider" ? () => handleBatchRetry(item.key) : undefined,
        })),
    };

    // ---- 表格列定义 ----
    const columns = useMemo(
        () => [
            getIndexColumn("序号", 60),
            {
                title: "文件名",
                dataIndex: "original_filename",
                key: "original_filename",
                width: 240,
                render: (text, record) => <InvoiceFilenameCell text={text} record={record} />,
            },
            {
                title: "账单号",
                key: "invoice_number",
                width: 120,
                ellipsis: true,
                render: (_, record) => {
                    const summary = record.invoice_summary;
                    if (!summary?.invoice_numbers?.length) return "—";

                    const numbers = summary.invoice_numbers;
                    if (numbers.length === 1) return numbers[0];

                    const shown = numbers.slice(0, 2).join(", ");
                    const rest = numbers.length - 2;
                    return (
                        <Tooltip title={numbers.join("\n")} overlayStyle={{ whiteSpace: "pre-line" }}>
                            <span>
                                {shown}
                                {rest > 0 ? ` +${rest}` : ""}
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                title: "金额",
                key: "amount",
                width: 180,
                align: "right",
                render: (_, record) => {
                    const summary = record.invoice_summary;
                    if (!summary) return "—";

                    const { computed_total, expected_total, currency } = summary;
                    const warnings = summary.warnings || [];
                    const hasConversionWarning = hasInvoiceConversionWarning(summary);
                    const computedNumber = Number(computed_total);
                    const expectedNumber = Number(expected_total);
                    const hasExpectedTotal = hasUsableExpectedTotal(expected_total);
                    const displayedNumber = hasExpectedTotal ? expectedNumber : computedNumber;
                    const totalsByCurrency = summary.is_multi_currency
                        ? Object.entries(summary.computed_totals_by_currency || {}).map(([itemCurrency, computedAmount]) => {
                            const expectedAmount = summary.expected_totals_by_currency?.[itemCurrency];
                            const amount = expectedAmount !== null && expectedAmount !== undefined
                                ? expectedAmount
                                : computedAmount;
                            return [itemCurrency, Number(amount)];
                        })
                        : [];
                    const formatted = Number.isFinite(displayedNumber) ? displayedNumber.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }) : "—";
                    const computedFormatted = Number.isFinite(computedNumber) ? computedNumber.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }) : "—";
                    const presentation = getInvoiceBalancePresentation(summary);

                    const tooltipLines = [
                        summary.is_multi_currency
                            ? `按币种金额: ${totalsByCurrency.map(([itemCurrency, amount]) => `${itemCurrency} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`).join("；")}`
                            : `明细合计: ${computedFormatted}`,
                        hasExpectedTotal ? `账单声明: ${expectedNumber.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : null,
                        presentation.annotation,
                        hasConversionWarning ? "存在金额折算提示，请查看明细" : null,
                        ...warnings.slice(0, 3).map((warning) => warning.message || warning.code),
                    ].filter(Boolean).join("\n");

                    return (
                        <Tooltip title={tooltipLines} overlayStyle={{ whiteSpace: "pre-line" }}>
                            <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>
                                {summary.is_multi_currency ? totalsByCurrency.map(([itemCurrency, amount]) => (
                                    <span key={itemCurrency} style={{ color: presentation.color || (hasConversionWarning ? "#fa8c16" : undefined) }}>
                                        {itemCurrency} {Number.isFinite(amount) ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                                        {hasConversionWarning && " !"}
                                    </span>
                                )) : (
                                    <span style={{ color: presentation.color || (hasConversionWarning ? "#fa8c16" : undefined) }}>
                                        {currency && <span style={{ marginRight: 2 }}>{currency}</span>}
                                        {formatted}
                                        {hasConversionWarning && " !"}
                                    </span>
                                )}
                                {presentation.annotation && (
                                    <span style={{ color: presentation.color, fontSize: 11, lineHeight: 1.2 }}>
                                        {presentation.annotation}
                                    </span>
                                )}
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                title: "进度",
                key: "progress",
                width: 110,
                render: (_, record) => <PipelineProgress status={record.status} />,
            },
            {
                title: "阶段",
                key: "stage",
                width: 100,
                render: (_, record) => {
                    const info = getStageInfo(record.status);
                    return (
                        <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                            <span style={{ fontWeight: 500 }}>{info.label}</span>
                            {info.subLabel && <span style={{ color: info.color, marginLeft: 4 }}>· {info.subLabel}</span>}
                        </span>
                    );
                },
            },
            {
                title: "状态",
                dataIndex: "status",
                key: "status",
                width: 180,
                render: (_, record) => <InvoiceStatusCell record={record} onOpenDrawer={onOpenDrawer} />,
            },
            {
                title: "更新时间",
                dataIndex: "updated_at",
                key: "updated_at",
                width: 140,
                render: (time) => (time ? new Date(time).toLocaleString() : "—"),
            },
            {
                title: "操作",
                key: "actions",
                width: 300,
                fixed: "right",
                render: (_, record) => <RowActionMenu record={record} onOpenDrawer={onOpenDrawer} onDelete={handleSingleDelete} onVoidWithReparse={handleOpenVoidWithReparse} />,
            },
        ],
        [onOpenDrawer, getIndexColumn, handleSingleDelete, handleOpenVoidWithReparse],
    );

    return (
        <div className="invoice-list-tab">
            {/* 顶部操作栏 */}
            <div className="invoice-list-tab__toolbar">
                {/* 三维复合筛选栏：阶段 | 状态 | 搜索 */}
                <div className="invoice-list-tab__filters">
                    {/* 维度 1: 阶段筛选 */}
                    {Object.entries(STAGE_FILTER_CATEGORIES).map(([key, cat]) => (
                        <button key={key} className={`filter-pill ${activeStageFilter === key ? "filter-pill--active" : ""}`} onClick={() => setActiveStageFilter(key)} style={activeStageFilter === key && cat.color ? { borderColor: cat.color, color: cat.color, background: cat.color + "0d" } : undefined}>
                            {cat.label}
                            <Badge
                                count={stageCounts[key]}
                                size="small"
                                style={{
                                    marginLeft: 4,
                                    backgroundColor: activeStageFilter === key ? cat.color || "#1890ff" : "#d9d9d9",
                                }}
                            />
                        </button>
                    ))}

                    {/* 分隔符 */}
                    <span className="filter-divider" />

                    {/* 维度 2: 状态快捷筛选 */}
                    {Object.entries(STATUS_QUICK_FILTERS).map(([key, cat]) => (
                        <button key={key} className={`filter-pill filter-pill--status ${activeStatusFilter === key ? "filter-pill--active" : ""}`} onClick={() => setActiveStatusFilter(key)} style={activeStatusFilter === key && cat.color ? { borderColor: cat.color, color: cat.color, background: cat.color + "0d" } : undefined}>
                            {cat.label}
                            {key !== "all" && (
                                <Badge
                                    count={statusCounts[key]}
                                    size="small"
                                    showZero
                                    style={{
                                        marginLeft: 4,
                                        backgroundColor: activeStatusFilter === key ? cat.color || "#1890ff" : "#d9d9d9",
                                    }}
                                />
                            )}
                        </button>
                    ))}

                    {/* 分隔符 */}
                    <span className="filter-divider" />

                    {/* 维度 3: 文本搜索 */}
                    <Input
                        placeholder="搜索文件名 / 账单号"
                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                        size="small"
                        style={{ width: 180, height: 28 }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                </div>

                {/* 右侧按钮 */}
                <Space>
                    <Tooltip title="刷新">
                        <Button icon={<ReloadOutlined />} onClick={refreshBatch} loading={batchLoading} size="small" />
                    </Tooltip>
                    <PermissionGuard permission="settlement_ap_invoice_data_source:update">
                        <Tooltip title={!batchValidationReadiness.ready && batchValidationReadiness.total > 0 ? `${batchValidationReadiness.ineligible.length} 张账单尚未完成验证航班` : batchValidationReadiness.total === 0 ? "批次内无账单" : "对整个批次执行整批核查"}>
                            <Button icon={<AuditOutlined />} size="small" disabled={!batchValidationReadiness.ready} onClick={triggerBatchValidation}>
                                整批核查
                            </Button>
                        </Tooltip>
                    </PermissionGuard>
                </Space>
            </div>

            {/* 批量操作栏 - fixed 悬浮定位（已内置于 BatchActionsBar 组件） */}
            {selectedRowKeys.length > 0 && (
                <PermissionGuard permission={["settlement_ap_invoice_data_source:update", "settlement_ap_invoice_data_source:delete", "settlement_ap_invoice:commit", "settlement_ap_reconciliation:update"]} requireAll={false}>
                    <BatchActionsBar
                        selectedCount={selectedRowKeys.length}
                        itemName="张账单"
                        onSelectAll={handleSelectAllVisible}
                        onClearSelection={clearSelection}
                        actions={[
                            {
                                key: "batch-settlement-currency",
                                label: "批量设置结算币种",
                                render: () => (
                                    <BatchSettlementCurrencyAction
                                        selectedRowKeys={selectedRowKeys}
                                        records={idsList}
                                        onSubmit={batchSetSettlementCurrency}
                                        onCompleted={clearSelection}
                                    />
                                ),
                            },
                            {
                                key: "batch-commit",
                                label: "批量审核通过",
                                render: () => (
                                    <PermissionGuard permission="settlement_ap_invoice:commit">
                                        <Button
                                            type="primary"
                                            size="small"
                                            ghost
                                            icon={<CheckCircleOutlined />}
                                            loading={batchActioning}
                                            disabled={!selectedRowKeys.some((id) => {
                                                const item = idsList.find((i) => i.id === id);
                                                return item && getDisplayStatus(item.status) === "REVIEW";
                                            })}
                                            onClick={handleBatchCommit}
                                            style={{ fontSize: "12px" }}
                                        >
                                            批量审核通过 ({selectedRowKeys.length})
                                        </Button>
                                    </PermissionGuard>
                                ),
                            },
                            {
                                key: "retry",
                                label: "批量重试",
                                render: () => (
                                    <Dropdown menu={retryMenuProps} trigger={["click"]}>
                                        <Button type="primary" size="small" ghost icon={<PlayCircleOutlined />} loading={batchActioning} style={{ fontSize: "12px" }}>
                                            批量重试 ({selectedRowKeys.length}) <DownOutlined style={{ fontSize: "10px", marginLeft: 4 }} />
                                        </Button>
                                    </Dropdown>
                                ),
                            },
                            {
                                key: "stop",
                                label: "批量停止",
                                icon: <StopOutlined />,
                                danger: true,
                                loading: batchActioning,
                                onClick: handleBatchStop,
                            },
                            {
                                key: "delete",
                                label: "批量删除",
                                render: () => (
                                    <PermissionGuard permission="settlement_ap_invoice_data_source:delete">
                                        <Button type="primary" size="small" ghost danger icon={<DeleteOutlined />} loading={batchActioning} onClick={handleBatchDelete} style={{ fontSize: "12px" }}>
                                            批量删除 ({selectedRowKeys.length})
                                        </Button>
                                    </PermissionGuard>
                                ),
                            },
                        ]}
                    />
                </PermissionGuard>
            )}

            {/* 表格 */}
            <Table
                rowKey="id"
                columns={columns}
                dataSource={filteredList}
                rowSelection={rowSelection}
                loading={batchLoading}
                pagination={false}
                size="small"
                scroll={{ x: 1180 }}
                ref={selectionTableRef}
                onRow={(record, index) => ({
                    ...getRowProps(record, index),
                    onClick: (e) => {
                        const target = e.target;
                        if (target.closest(".ant-checkbox-wrapper") || target.closest(".row-number-with-checkbox") || target.closest(".ant-btn") || target.closest(".ant-dropdown-trigger") || target.closest(".ant-dropdown") || target.closest(".ant-popover")) {
                            return;
                        }
                        onOpenDrawer?.(record);
                    },
                    style: { cursor: "pointer" },
                })}
                locale={{ emptyText: batchNumber ? "该批次暂无账单" : "请选择批次" }}
            />

            <ConfirmableRiskWarningModal
                open={Boolean(batchRiskWarningModal)}
                title="批量审核存在可确认风险"
                summary={batchRiskWarningModal && (
                    <div>
                        成功: <span style={{ color: "#52c41a", fontWeight: 600 }}>{batchRiskWarningModal.succeeded}</span> 张，需确认: <span style={{ color: "#fa8c16", fontWeight: 600 }}>{batchRiskWarningModal.items.length}</span> 张
                        {batchRiskWarningModal.blockingFailedItems.length > 0 && (
                            <>
                                ，仍需修正: <span style={{ color: "#ff4d4f", fontWeight: 600 }}>{batchRiskWarningModal.blockingFailedItems.length}</span> 张
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ fontWeight: 500, marginBottom: 4 }}>仍需修正的失败项：</div>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {batchRiskWarningModal.blockingFailedItems.map((item) => {
                                            const record = idsList.find((candidate) => candidate.id === item.ids_id);
                                            return (
                                                <li key={item.ids_id} style={{ marginBottom: 4 }}>
                                                    <span style={{ fontWeight: 500 }}>{record?.original_filename || item.ids_id}</span>
                                                    <span style={{ color: "#ff4d4f", marginLeft: 6 }}>{item.message}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                )}
                warnings={batchRiskWarningModal?.warnings || []}
                confirmText={batchRiskWarningModal ? `长按 5 秒强制通过 ${batchRiskWarningModal.items.length} 张` : "长按 5 秒强制通过"}
                cancelText={batchRiskWarningModal?.blockingFailedItems?.length > 0 ? "关闭" : "取消"}
                loading={batchActioning}
                onCancel={() => setBatchRiskWarningModal(null)}
                onConfirm={() => {
                    const modalState = batchRiskWarningModal;
                    if (!modalState) return undefined;
                    setBatchRiskWarningModal(null);
                    return runBatchCommitRef.current?.(
                        modalState.items.map((item) => item.ids_id),
                        {
                            ignoreWarnings: true,
                            previousBlockingFailedItems: modalState.blockingFailedItems,
                        },
                    );
                }}
            />

            <VoidConfirmModal
                open={voidModal.open}
                title="作废并重发"
                targetLabel={voidModal.record?.original_filename || voidModal.record?.id}
                impact={voidModal.impact}
                impactLoading={voidImpactLoading}
                confirmText="确认作废并重发"
                submitting={batchActioning}
                onCancel={() => setVoidModal({ open: false, record: null, impact: null })}
                onConfirm={handleConfirmVoidWithReparse}
            />
        </div>
    );
};

export default InvoiceListTab;
